import React from 'react'
import type { TFunction } from 'i18next'
import type { ElectricityAnalysis } from '@popety-io/types'
import { PriceEvolutionChart } from '../charts/PriceEvolutionChart'
import { getEvolutionDisplay } from '../electricityService'
import {
  TabContentContainer,
  ChartContainer,
  ChartTitle,
  ChartSubtitle,
  EvolutionGrid,
  EvolutionCard,
  EvolutionLabel,
  EvolutionValue,
} from '../ElectricitySection.styles'

export interface TrendsTabProps {
  data: ElectricityAnalysis | null
  t: TFunction
}

export const TrendsTab: React.FC<TrendsTabProps> = ({ data, t }) => {
  if (!data?.current_data) {
    return (
      <TabContentContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t('states.noHistoricalData')}
        </div>
      </TabContentContainer>
    )
  }

  const { current_data } = data
  const gridOperators = current_data.grid_operators || []
  const hasOperatorHistory = gridOperators.some(
    (op) => op.yearly_data && op.yearly_data.length > 0
  )

  // Evolution summary data
  const evo1 = getEvolutionDisplay(
    current_data.total_price_min_evolution_1_year
  )
  const evo2 = getEvolutionDisplay(
    current_data.total_price_min_evolution_2_year
  )
  const evo5 = getEvolutionDisplay(
    current_data.total_price_min_evolution_5_year
  )
  const evo10 = getEvolutionDisplay(
    current_data.total_price_min_evolution_10_year
  )

  return (
    <TabContentContainer>
      {/* Evolution Summary Cards */}
      <EvolutionGrid>
        <EvolutionCard>
          <EvolutionLabel>{t('evolution.1year')}</EvolutionLabel>
          <EvolutionValue
            $positive={evo1.value === 'N/A' ? undefined : evo1.isPositive}
          >
            {evo1.value}
          </EvolutionValue>
        </EvolutionCard>
        <EvolutionCard>
          <EvolutionLabel>{t('evolution.2year')}</EvolutionLabel>
          <EvolutionValue
            $positive={evo2.value === 'N/A' ? undefined : evo2.isPositive}
          >
            {evo2.value}
          </EvolutionValue>
        </EvolutionCard>
        <EvolutionCard>
          <EvolutionLabel>{t('evolution.5year')}</EvolutionLabel>
          <EvolutionValue
            $positive={evo5.value === 'N/A' ? undefined : evo5.isPositive}
          >
            {evo5.value}
          </EvolutionValue>
        </EvolutionCard>
        <EvolutionCard>
          <EvolutionLabel>{t('evolution.10year')}</EvolutionLabel>
          <EvolutionValue
            $positive={evo10.value === 'N/A' ? undefined : evo10.isPositive}
          >
            {evo10.value}
          </EvolutionValue>
        </EvolutionCard>
      </EvolutionGrid>

      {/* Price Evolution Chart */}
      {hasOperatorHistory && (
        <ChartContainer>
          <ChartTitle>{t('charts.priceEvolution.title')}</ChartTitle>
          <ChartSubtitle>{t('charts.priceEvolution.subtitle')}</ChartSubtitle>
          <PriceEvolutionChart gridOperators={gridOperators} t={t} />
        </ChartContainer>
      )}

      {!hasOperatorHistory && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t('states.noHistoricalData')}
        </div>
      )}
    </TabContentContainer>
  )
}
